import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useCreateCustomApiCaller } from "@/api/onboarding/create-custom-api-caller.ts";
import { useUpdateCustomApiCaller } from "@/api/onboarding/update-custom-api-caller.ts";
import { useCreateWebSettings } from "@/api/onboarding/create-web-settings.ts";
import { useUpdateWebSettings } from "@/api/onboarding/update-web-settings.ts";
import { useGetCustomApiCallRouter } from "@/api/chat-settings/get-custom-api-settings.ts";
import { useEffect } from "react";

// Define the schema with Zod
const formSchema = z.object({
    x_api_key: z.string().optional(),
    products_api_url: z.string().url("Must be a valid URL"),
    order_details_by_id_api_url: z.string().optional(),
    get_orders_by_email_api_url: z.string().optional(),
    cart_local_storage_key: z.string().optional(),
    cart_url: z.string().optional(),
    checkout_url: z.string().optional(),
    website_url: z.string().min(1, "Site URL is required"),
    search_categories_by_name_api_url: z.string().min(1, "Search categories by name API URL is required"),
    search_products_by_category_name_api_url: z.string().min(1, "Search products by category name API URL is required"),
    all_category_api_url: z.string().min(1, "All category API URL is required"),
});

// Define form data type based on schema
type FormData = z.infer<typeof formSchema>;

type Props = {
    id: string | undefined | null;
    website_url: string | undefined | null;
    mode?: string;
    locator?: string;
};

const CustomWebSettingsForm: React.FC<Props> = ({ id, website_url, mode, locator }) => {
    // Initialize the form with react-hook-form and Zod resolver
    const form = useForm<FormData>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            x_api_key: "",
            products_api_url: "http://localhost:8085/api/product/",
            order_details_by_id_api_url: "",
            get_orders_by_email_api_url: "",
            cart_local_storage_key: "",
            cart_url: "",
            checkout_url: "",
            website_url: "",
            search_categories_by_name_api_url: "",
            search_products_by_category_name_api_url: "",
            all_category_api_url: "",
        },
    });

    const { mutate: createCustomApiCaller } = useCreateCustomApiCaller();
    const { mutate: updateCustomApiCaller } = useUpdateCustomApiCaller();
    const { mutate: createWebSettings } = useCreateWebSettings();
    const { mutate: updateWebSettings } = useUpdateWebSettings();
    const { data: customApiCallRotuer } = useGetCustomApiCallRouter();

    {
        console.log({ customApiCallRotuer });
    }

    useEffect(() => {
        if (customApiCallRotuer) {
            form.setValue("cart_local_storage_key", customApiCallRotuer?.data?.cart_local_storage_key || "");
            form.setValue("cart_url", customApiCallRotuer?.data?.cart_url || "");
            form.setValue("order_details_by_id_api_url", customApiCallRotuer?.data?.order_details_by_id_api_url || "");
            form.setValue("x_api_key", customApiCallRotuer?.data?.x_api_key || "");
            form.setValue("products_api_url", customApiCallRotuer?.data?.products_api_url || "");
            form.setValue("get_orders_by_email_api_url", customApiCallRotuer?.data?.get_orders_by_email_api_url || "");
            form.setValue("checkout_url", customApiCallRotuer?.data?.checkout_url || "");
            form.setValue("website_url", website_url || "");
        }
    }, [customApiCallRotuer]);
    // Handle form submission
    const onSubmit = (data: FormData) => {
        const { website_url, ...rest } = data;
        if (customApiCallRotuer?.data?.id) {
            updateCustomApiCaller({ id: customApiCallRotuer?.data?.id, ...rest });
            if (id) {
                updateWebSettings({ website_url: website_url, website_type: "custom", mode, locator });
                return;
            } else {
                createWebSettings({ website_url: website_url, website_type: "custom", mode, locator });
                return;
            }
        }
        createCustomApiCaller(rest);
        // Add your submission logic here
    };

    return (
        <div className="my-4">
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 grid grid-cols-2 gap-4">
                    <FormField
                        control={form.control}
                        name="website_url"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Website URL *</FormLabel>
                                <FormControl>
                                    <Input className={"py-6"} placeholder="Enter Website URL" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="x_api_key"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>X API Key</FormLabel>
                                <FormControl>
                                    <Input className={"py-6"} placeholder="Enter X API key" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="products_api_url"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Products API URL *</FormLabel>
                                <FormControl>
                                    <Input className={"py-6"} placeholder="Enter products API URL" {...field} required />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="order_details_by_id_api_url"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Order Details by ID API URL</FormLabel>
                                <FormControl>
                                    <Input className={"py-6"} placeholder="Enter order details API URL" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="get_orders_by_email_api_url"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Get Orders by Email API URL</FormLabel>
                                <FormControl>
                                    <Input className={"py-6"} placeholder="Enter orders by email API URL" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="cart_local_storage_key"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Cart Local Storage Key</FormLabel>
                                <FormControl>
                                    <Input className={"py-6"} placeholder="Enter cart local storage key" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="cart_url"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Cart URL</FormLabel>
                                <FormControl>
                                    <Input className={"py-6"} placeholder="Enter cart URL" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="checkout_url"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Checkout URL</FormLabel>
                                <FormControl>
                                    <Input className={"py-6"} placeholder="Enter checkout URL" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="search_categories_by_name_api_url"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Search Categories by Name API URL *</FormLabel>
                                <FormControl>
                                    <Input className={"py-6"} placeholder="Enter search categories by name API URL" {...field} required />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="search_products_by_category_name_api_url"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Search Products by Category Name API URL *</FormLabel>
                                <FormControl>
                                    <Input className={"py-6"} placeholder="Enter search products by category name API URL" {...field} required />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="all_category_api_url"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>All Category API URL *</FormLabel>
                                <FormControl>
                                    <Input className={"py-6"} placeholder="Enter all category API URL" {...field} required />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className={"col-span-2"}>
                        <Button type="submit">Save Configuration</Button>
                    </div>
                </form>
            </Form>
        </div>
    );
};

export default CustomWebSettingsForm;
